import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, CalendarDays, Car, ClipboardPlus, CloudSun, Compass, GraduationCap, Map, MapPinned, Megaphone, ShieldAlert, ShieldCheck, Newspaper, StickyNote, Wrench, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, hasRole, MANUTENTORE_ROLE, MARE_ROLES, rolesOf, VIG_REPORT_ROLES, VIG_SUPERVISOR_ROLE, BANCHINA_ROLE, TOWER_ROLE } from "../lib/roles";
import { usePerm } from "../context/PermContext";
import { WeekShifts } from "../components/WeekShifts";
import { Panel, SevBadge } from "../components/common";
import { DeadlinesWidget } from "../components/DeadlinesWidget";
import { TodayShift } from "../components/TodayShift";
import { OnShiftWithMe } from "../components/OnShiftWithMe";
import { OnShiftToday } from "../components/OnShiftToday";
import { BirthdaysToday } from "../components/BirthdaysToday";
import { useMode } from "../lib/mode";

const HERO = "https://customer-assets.emergentagent.com/job_roma-port-crew/artifacts/5etyckmx_P1010207.JPG";

const Section = ({ title, to, children }) => (
  <section className="mb-10">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-display text-xl font-bold text-[var(--navy)]">{title}</h2>
      {to && <Link to={to} className="text-sm text-[var(--teal)] font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid={`see-all-${title.toLowerCase().replace(/\s/g, "-")}`}>Vedi tutti <ArrowRight className="w-4 h-4" /></Link>}
    </div>
    {children}
  </section>
);

const DIRECTOR_HOME = [
  { to: "/meteo", label: "Meteo Porto", icon: CloudSun },
  { to: "/marina", label: "Mappa Porto", icon: Map },
  { to: "/news", label: "News", icon: Newspaper },
  { to: "/avvisi", label: "Comunicazioni e avvisi", icon: Megaphone },
  { to: "/postit", label: "I miei Post-it", icon: StickyNote },
  { to: "/scadenziario", label: "Scadenziario", icon: CalendarClock },
  { to: "/manutenzioni", label: "Manutenzioni", icon: Wrench },
];

function quickActions(user, can, admin, mySettori, local, park) {
  if (admin) return park ? [{ to: "/parcheggi", label: "Richiedi parcheggio", icon: Car }, ...DIRECTOR_HOME] : DIRECTOR_HOME;
  const a = [];
  if (park) a.push({ to: "/parcheggi", label: "Richiedi parcheggio", icon: Car });
  if (local && hasRole(user, [VIG_SUPERVISOR_ROLE])) a.push({ to: "/vigilanza/personale", label: "Personale Vigilanza", icon: ShieldCheck });
  if (!admin && mySettori.length > 0 && can("report.edit")) a.push({ to: "/report/shift-new", label: "Invia report", icon: ClipboardPlus });
  if (can("report.edit") && hasRole(user, VIG_REPORT_ROLES)) a.push({ to: "/vigilanza/new", label: "Report turno Vigilanza", icon: ShieldCheck });
  if (can("comms.vig")) a.push({ to: "/comunica-vigilanza", label: "Comunica Vigilanza", icon: ShieldAlert });
  if (can("comms.alert")) a.push({ to: "/alert/new", label: "Invia Alert", icon: AlertTriangle });
  if (hasRole(user, MARE_ROLES) && (can("turni.view") || can("turni.ferie") || can("turni.straordinari"))) a.push({ to: "/turni", label: "Turni, Ferie e permessi", icon: CalendarDays });
  a.push({ to: "/meteo", label: "Meteo Porto", icon: CloudSun });
  if (can("marina.view")) a.push({ to: "/marina", label: "Mappa Porto", icon: Map });
  if (admin) a.push({ to: "/marina/admin", label: "Mappa Admin", icon: MapPinned });
  a.push({ to: "/news", label: "News", icon: Newspaper });
  a.push({ to: "/postit", label: "I miei Post-it", icon: StickyNote });
  if (can("corsi.view") || can("corsi.edit")) a.push({ to: "/corsi", label: "Corsi e brevetti", icon: GraduationCap });
  if (can("scadenzario.view")) a.push({ to: "/scadenziario", label: "Scadenziario", icon: CalendarClock });
  if (can("manutenzioni.view")) a.push({ to: "/manutenzioni", label: "Manutenzioni", icon: Wrench });
  if ((admin && can("manutenzioni.view")) || hasRole(user, [MANUTENTORE_ROLE])) a.push({ to: "/manutenzioni/interventi", label: "Alert ricevuti", icon: Megaphone });
  if (can("nostromo.tasks")) a.push({ to: "/nostromo/tasks", label: "Assegna lavori da fare", icon: Compass });
  return a;
}

export default function Home() {
  const { user: realUser } = useAuth();
  const { can, admin, viewUser: user, mySettori } = usePerm();
  const mode = useMode();
  const [d, setD] = useState({ shifts: [], comms: [], tasks: [], alerts: [], todo: [] });
  const [park, setPark] = useState(false);
  useEffect(() => { api("/web/parking/me").then((r) => setPark(!!r.can_request)).catch(() => setPark(false)); }, [realUser, user?.id]);

  useEffect(() => {
    (async () => {
      const [s, c, t, a] = await Promise.all([api("/shifts/me"), api("/communications"), api("/tasks"), api("/alerts")].map((p) => p.catch(() => ({}))));
      let todo = [];
      if (hasRole(user, [BANCHINA_ROLE])) todo = (await api("/web/shift-reports/todo").catch(() => ({}))).items || [];
      setD({ shifts: s.shifts || [], comms: c.communications || [], tasks: t.tasks || [], alerts: (a.alerts || []).filter((x) => !x.resolved), todo });
    })();
  }, [realUser, user?.mansione]);

  const openTasks = d.tasks.filter((t) => !t.completed);
  const inShifts = d.shifts.some((s) => (s.orario || "").trim());

  return (
    <div>
      <div className="relative rounded-3xl overflow-hidden mb-10 grain fade-up" data-testid="home-hero">
        <img src={HERO} alt="Porto" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--navy-deep)]/95 via-[var(--navy)]/70 to-transparent" />
        <div className="relative p-8 sm:p-12 text-white">
          <p className="eyebrow !text-white/70 mb-3">{rolesOf(user).join(" · ")}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Benvenuto a bordo,<br />{user?.nome}</h1>
        </div>
      </div>

      {!admin && !(user?.mansioni || [user?.mansione]).some((m) => (m || "").toLowerCase() === "utente porto") && <TodayShift shifts={d.shifts} />}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-12 stagger">
        {quickActions(user, can, admin, mySettori, mode?.mode === "local", park).map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} className="panel panel-hover !p-5 flex flex-col gap-4" data-testid={`quick-${to.replace(/\//g, "-").slice(1)}`}>
            <span className="w-10 h-10 rounded-xl bg-[var(--teal-soft)] flex items-center justify-center"><Icon className="w-5 h-5 text-[var(--teal)]" /></span>
            <span className="font-semibold text-[var(--navy)] text-sm">{label}</span>
          </Link>
        ))}
      </div>

      {admin && <BirthdaysToday />}
      {admin && <OnShiftToday />}

      {admin && <DeadlinesWidget />}

      {d.alerts.length > 0 && (
        <Section title="Alert attivi" to="/avvisi">
          <div className="space-y-3">{d.alerts.slice(0, 3).map((a) => (
            <Link key={a.id} to={`/alert/${a.id}`} className="panel panel-hover flex items-start gap-4 !p-4" data-testid={`home-alert-${a.id}`}>
              <Megaphone className="w-5 h-5 text-[var(--red)] mt-0.5 shrink-0" />
              <div className="min-w-0"><div className="flex items-center gap-2 mb-1"><SevBadge sev={a.severita} /><span className="font-semibold text-[var(--navy)]">{a.categoria}</span></div>
                <p className="text-sm text-slate-600 truncate">{a.messaggio}</p></div>
            </Link>))}
          </div>
        </Section>
      )}

      {d.todo.length > 0 && (
        <Section title="Cose da fare">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="home-todo">{d.todo.map((x, i) => (
            <Panel key={i} className="!p-4 border-l-4 !border-l-[var(--teal)]" data-testid={`home-todo-${i}`}>
              <p className="text-xs text-slate-500 mb-2"><span className="tag tag-teal mr-2">{x.fonte}</span>{x.author_name} · {fmtDate(x.data)}{x.turno ? ` · ${x.turno}` : ""}</p>
              <p className="whitespace-pre-wrap text-sm text-[var(--navy)]">{x.testo}</p>
            </Panel>))}
          </div>
        </Section>
      )}

      <OnShiftWithMe heading={<h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4">Chi è di turno con me oggi</h2>} />
      {inShifts && <Section title="Turni Settimanali" to="/turni"><WeekShifts shifts={d.shifts} /></Section>}

      {inShifts && openTasks.length > 0 && (
        <Section title="Compiti assegnati" to="/avvisi">
          <div className="grid sm:grid-cols-2 gap-3">{openTasks.slice(0, 4).map((t) => (
            <Panel key={t.id} className="!p-4"><p className="font-semibold text-[var(--navy)]">{t.titolo}</p><p className="text-sm text-slate-600 mt-1">{t.descrizione}</p>
              {(t.data_scadenza || t.data_lavoro) && <p className="text-xs text-slate-500 mt-2">{t.data_lavoro ? "Giorno lavoro: " : "Scadenza: "}{fmtDate(t.data_lavoro || t.data_scadenza)}</p>}</Panel>))}
          </div>
        </Section>
      )}

      <Section title="Comunicazioni" to="/avvisi">
        {d.comms.length === 0 ? <Panel><p className="font-semibold text-[var(--navy)]">Nessuna comunicazione</p><p className="text-sm text-slate-500">Le comunicazioni del personale appariranno qui.</p></Panel> : (
          <div className="space-y-3">{d.comms.slice(0, 3).map((c) => (
            <Panel key={c.id} className="!p-4" data-testid={`home-comm-${c.id}`}><p className="font-semibold text-[var(--navy)]">{c.titolo}</p><p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.messaggio}</p>
              <p className="text-xs text-slate-400 mt-2">{c.from_name || c.author_name} · {fmtDate(c.created_at, true)}</p></Panel>))}
          </div>)}
      </Section>
    </div>
  );
}
